import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, PhoneOff, Mic, MicOff, Loader2, BrainCircuit, Clock, Radio } from "lucide-react";
import { api } from "@/context/AuthContext";
import { speak, listen, sttAvailable, stopSpeaking } from "@/lib/voice";
import { LegalConsentModal } from "@/components/LegalConsentModal";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}`;
const ROBOT = "/robots/hero_robot.png";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.max(0, s % 60)).padStart(2, "0")}`;

const Waveform = ({ active, color = "#00FFAA" }) => (
  <div className="flex items-end justify-center gap-1 h-12" data-testid="neural-waveform-live">
    {[14, 28, 40, 22, 34, 18, 42, 26, 32, 16, 38, 20].map((h, i) => (
      <motion.span
        key={i}
        animate={active ? { height: [h * 0.35, h, h * 0.5, h * 0.85, h * 0.35] } : { height: 5 }}
        transition={{ duration: 1 + (i % 4) * 0.16, repeat: active ? Infinity : 0, ease: "easeInOut", delay: i * 0.06 }}
        className="w-1.5 rounded-full"
        style={{ height: active ? h : 5, background: `linear-gradient(to top, ${color}, #00F5FF)` }}
      />
    ))}
  </div>
);

export const NeuralLink = ({ t, lang, user, setUser }) => {
  const n = t.neural;
  const [status, setStatus] = useState("idle"); // idle | connecting | live | ended
  const [mode, setMode] = useState(null); // "realtime" | "browser"
  const [muted, setMuted] = useState(false);
  const [talking, setTalking] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [memories, setMemories] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [buyingPack, setBuyingPack] = useState(null);

  const [showLegal, setShowLegal] = useState(false);
  const pendingPackRef = useRef(null);

  const buyPack = async (packId) => {
    if (!user.legal_accepted) {
      pendingPackRef.current = packId;
      setShowLegal(true);  // legal consent happens AT PAYMENT time
      return;
    }
    setBuyingPack(packId);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: packId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setBuyingPack(null);
    }
  };

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const audioRef = useRef(null);
  const transcriptRef = useRef([]);
  const timerRef = useRef(null);
  const usageRef = useRef(0);
  const liveRef = useRef(false);
  const browserLoopRef = useRef(false);

  useEffect(() => () => endLink(true), []); // eslint-disable-line react-hooks/exhaustive-deps

  const startTimer = (initialLeft) => {
    setSecondsLeft(initialLeft);
    timerRef.current = setInterval(async () => {
      usageRef.current += 1;
      setSecondsLeft((s) => {
        const nxt = (s ?? 1) - 1;
        if (nxt <= 0) lowPowerCutoff();
        return Math.max(0, nxt);
      });
      if (usageRef.current >= 30) {
        const batch = usageRef.current;
        usageRef.current = 0;
        try {
          const { data } = await api.post("/neural/usage", { seconds: batch });
          if (data.cut_off) lowPowerCutoff();
        } catch { /* noop */ }
      }
    }, 1000);
  };

  const lowPowerCutoff = async () => {
    if (!liveRef.current) return;
    liveRef.current = false;
    if (audioRef.current) {
      let v = 1.0; // smooth fade-out
      const fade = setInterval(() => {
        v = Math.max(0, v - 0.15);
        if (audioRef.current) audioRef.current.volume = v;
        if (v <= 0) clearInterval(fade);
      }, 150);
    }
    await speak(n.lowPower, lang, { rate: 0.95 });
    endLink();
    toast.error(n.lowPowerToast);
  };

  /* ---------- Premium: OpenAI Realtime over WebRTC (GA flow) ---------- */
  const startRealtime = async (context) => {
    const tokenRes = await fetch(`${API_BASE}/api/v1/realtime/session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("a4u_token") || ""}`,
      },
      body: JSON.stringify({ instructions: context.system_prompt, voice: "marin" }),
    });
    const tokenData = await tokenRes.json();
    const ephemeral = tokenData.client_secret?.value;
    const rtModel = tokenData.model || "gpt-realtime-mini";
    if (!ephemeral) throw new Error("no session token");

    const pc = new RTCPeerConnection();
    pcRef.current = pc;
    const audioEl = new Audio();
    audioEl.autoplay = true;
    audioRef.current = audioEl;
    pc.ontrack = (e) => { audioEl.srcObject = e.streams[0]; setTalking(true); };

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    localStreamRef.current = stream;
    stream.getTracks().forEach((tr) => pc.addTrack(tr, stream));

    const dc = pc.createDataChannel("oai-events");
    dc.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data);
        if ((ev.type === "response.audio_transcript.done" || ev.type === "response.output_audio_transcript.done") && ev.transcript) {
          transcriptRef.current.push(`${context.avatar_name}: ${ev.transcript}`);
        }
        if (ev.type === "conversation.item.input_audio_transcription.completed" && ev.transcript) {
          transcriptRef.current.push(`Human: ${ev.transcript}`);
        }
        if (ev.type === "output_audio_buffer.started") setTalking(true);
        if (ev.type === "output_audio_buffer.stopped") setTalking(false);
      } catch { /* noop */ }
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const res = await fetch(`${API_BASE}/api/v1/realtime/negotiate`, {
      method: "POST",
      body: offer.sdp,
      headers: { "Content-Type": "application/sdp", "X-Realtime-Token": ephemeral, "X-Realtime-Model": rtModel },
    });
    if (!res.ok) throw new Error("negotiate failed");
    const { sdp: answerSdp } = await res.json();
    await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
    setMode("realtime");
  };

  /* ---------- Fallback: browser STT + LLM + comic TTS ---------- */
  const browserLoop = async (context) => {
    setMode("browser");
    browserLoopRef.current = true;
    const vp = context.voice ? { pitch: context.voice.pitch, rate: context.voice.rate } : {};
    await speak(n.browserGreeting.replace("{avatar}", context.avatar_name), lang, vp);
    while (browserLoopRef.current && liveRef.current) {
      if (!sttAvailable()) break;
      setTalking(false);
      const heard = await listen(lang);
      if (!browserLoopRef.current) break;
      if (!heard) continue;
      transcriptRef.current.push(`Human: ${heard}`);
      try {
        setTalking(true);
        const { data } = await api.post("/neural/chat", { text: heard, lang });
        transcriptRef.current.push(`${data.avatar_name}: ${data.reply}`);
        await speak(data.reply, lang, vp);
      } catch { /* noop */ }
    }
  };

  const startLink = async () => {
    setStatus("connecting");
    setMemories(null);
    transcriptRef.current = [];
    try {
      const { data: context } = await api.get(`/neural/context?lang=${lang}`);
      setCtx(context);
      if (context.voice_seconds_left <= 0) {
        setStatus("idle");
        toast.error(n.quotaOut);
        return;
      }
      liveRef.current = true;
      try {
        await startRealtime(context);
      } catch (err) {
        console.warn("Realtime unavailable, using browser voice mode:", err);
        browserLoop(context);
      }
      setStatus("live");
      startTimer(context.voice_seconds_left);
    } catch (e) {
      setStatus("idle");
      toast.error(n.connectError);
    }
  };

  const toggleMute = () => {
    const stream = localStreamRef.current;
    if (stream) stream.getAudioTracks().forEach((tr) => { tr.enabled = muted; });
    if (mode === "browser" && !muted) browserLoopRef.current = false;
    if (mode === "browser" && muted && liveRef.current && ctx) { browserLoopRef.current = true; browserLoop(ctx); }
    setMuted((m) => !m);
  };

  const endLink = useCallback(async (silent = false) => {
    liveRef.current = false;
    browserLoopRef.current = false;
    stopSpeaking();
    clearInterval(timerRef.current);
    if (usageRef.current > 0) {
      const batch = usageRef.current;
      usageRef.current = 0;
      api.post("/neural/usage", { seconds: batch }).catch(() => {});
    }
    try { pcRef.current?.close(); } catch { /* noop */ }
    try { localStreamRef.current?.getTracks().forEach((tr) => tr.stop()); } catch { /* noop */ }
    pcRef.current = null;
    localStreamRef.current = null;
    if (silent) return;
    setStatus("ended");
    setTalking(false);
    const transcript = transcriptRef.current.join("\n");
    if (transcript.length > 20) {
      try {
        const { data } = await api.post("/neural/end-call", { text: transcript, lang });
        setMemories(data.memories || []);
      } catch { setMemories([]); }
    } else {
      setMemories([]);
    }
  }, [lang]);

  const live = status === "live";

  return (
    <div className="max-w-xl mx-auto" data-testid="neural-link-section">
      {showLegal && (
        <LegalConsentModal lang={lang} user={user} setUser={setUser}
          onDone={() => { setShowLegal(false); const pk = pendingPackRef.current; pendingPackRef.current = null; if (pk) buyPack(pk); }}
          onCancel={() => setShowLegal(false)} />
      )}
      <p className="text-sm text-[#A0A0B0] -mt-4 mb-8 text-center">{n.sub}</p>

      <div className="relative bg-[#0F0F1A]/80 backdrop-blur-2xl border border-[#00FFAA]/20 rounded-[2.5rem] p-8 overflow-hidden shadow-[0_0_45px_rgba(0,255,170,0.1)]">
        <div className="absolute -top-20 -right-20 w-52 h-52 bg-[#00FFAA] blur-[110px] opacity-15 pointer-events-none" />

        {/* Status bar */}
        <div className="flex items-center justify-between mb-8">
          <span className={`inline-flex items-center gap-2 text-[9px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${
            live ? "text-[#00FFAA] border-[#00FFAA]/40 bg-[#00FFAA]/8" : "text-white/40 border-white/10 bg-white/5"
          }`} data-testid="neural-status">
            <Radio size={11} /> {live ? (mode === "realtime" ? n.statusLiveRt : n.statusLiveBrowser) : status === "connecting" ? n.statusConnecting : n.statusIdle}
          </span>
          {secondsLeft !== null && (
            <span className={`inline-flex items-center gap-1.5 text-[10px] font-black px-3 py-1.5 rounded-full border ${
              secondsLeft < 60 ? "text-[#FF4444] border-[#FF4444]/40 bg-[#FF4444]/8 animate-pulse" : "text-[#00F5FF] border-[#00F5FF]/30 bg-[#00F5FF]/8"
            }`} data-testid="neural-countdown">
              <Clock size={11} /> {fmt(secondsLeft)}
            </span>
          )}
        </div>

        {/* Robot */}
        <div className="relative flex justify-center mb-4">
          {live && (
            <>
              <motion.span animate={{ scale: [1, 1.35, 1], opacity: [0.5, 0, 0.5] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }} className="absolute w-40 h-40 rounded-full border-2 border-[#00FFAA]/50" />
              <motion.span animate={{ scale: [1, 1.65, 1], opacity: [0.3, 0, 0.3] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut", delay: 0.5 }} className="absolute w-40 h-40 rounded-full border border-[#00FFAA]/30" />
            </>
          )}
          <motion.img
            src={ROBOT}
            alt="Neural Link avatar"
            animate={live ? { y: [0, -8, 0], rotate: talking ? [0, 2, -2, 0] : 0 } : { y: [0, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className={`relative w-36 h-36 object-contain transition-all duration-700 ${live ? "drop-shadow-[0_0_30px_rgba(0,255,170,0.55)]" : "opacity-70 drop-shadow-[0_0_18px_rgba(0,245,255,0.25)]"}`}
            data-testid="neural-robot"
          />
        </div>
        <p className="text-center font-heading font-black text-lg mb-6">{user.avatar_name || "—"}</p>

        <Waveform active={live && (talking || !muted)} />

        {/* Controls */}
        <div className="flex items-center justify-center gap-6 mt-8">
          {live && (
            <button
              data-testid="neural-mute-btn"
              onClick={toggleMute}
              className={`w-13 h-13 p-3.5 rounded-full border transition-all ${
                muted ? "bg-[#FF4444]/15 border-[#FF4444]/50 text-[#FF4444]" : "bg-white/5 border-white/15 text-white/70 hover:text-white"
              }`}
            >
              {muted ? <MicOff size={20} /> : <Mic size={20} />}
            </button>
          )}
          {live ? (
            <button
              data-testid="neural-end-btn"
              onClick={() => endLink()}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-heading font-black text-xs uppercase tracking-widest bg-[#FF4444] shadow-[0_0_25px_rgba(255,68,68,0.5)] hover:shadow-[0_0_40px_rgba(255,68,68,0.75)] transition-all"
            >
              <PhoneOff size={16} /> {n.end}
            </button>
          ) : (
            <button
              data-testid="neural-start-btn"
              onClick={startLink}
              disabled={status === "connecting"}
              className="inline-flex items-center gap-3 px-10 py-5 rounded-full font-heading font-black text-sm uppercase tracking-[0.15em] bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] text-[#05050A] shadow-[0_0_30px_rgba(0,255,170,0.5)] hover:shadow-[0_0_50px_rgba(0,255,170,0.8)] hover:-translate-y-1 transition-all disabled:opacity-60 animate-neon-pulse"
            >
              {status === "connecting" ? <Loader2 size={18} className="animate-spin" /> : <Phone size={18} className="fill-[#05050A]" />}
              {status === "connecting" ? n.connecting : n.start}
            </button>
          )}
        </div>
      </div>

      {/* Buy voice minutes — $0.20/min pay-as-you-go */}
      <div className="mt-6 bg-[#0F0F1A]/70 border border-[#FFD700]/25 rounded-3xl p-5" data-testid="voice-packs-section">
        <p className="text-xs font-black text-[#FFD700] mb-1">{user.plus_active ? n.buyTitlePlus : n.buyTitle}</p>
        <p className="text-[10px] text-white/45 mb-4">{n.buySub}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          {Object.entries(user.plus_active ? n.packsPlus : n.packs).map(([packId, label]) => (
            <button
              key={packId}
              data-testid={`voice-pack-${packId}`}
              onClick={() => buyPack(packId)}
              disabled={buyingPack}
              className="py-3.5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-[#05050A] bg-gradient-to-r from-[#FFD700] to-[#FFC83C] shadow-[0_0_16px_rgba(255,215,0,0.35)] hover:shadow-[0_0_26px_rgba(255,215,0,0.6)] hover:-translate-y-0.5 transition-all disabled:opacity-60"
            >
              {buyingPack === packId ? "..." : label}
            </button>
          ))}
        </div>
      </div>

      {/* Extracted memories after call */}
      {memories !== null && (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mt-6 bg-[#05050A]/70 border border-[#00F5FF]/20 rounded-3xl p-5" data-testid="neural-memories-result">
          <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#00F5FF] mb-3 flex items-center gap-2">
            <BrainCircuit size={12} /> {n.memoriesAnchored}
          </p>
          {memories.length === 0 ? (
            <p className="text-xs text-white/40">{n.noMemories}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {memories.map((m) => (
                <span key={m.id} className="text-[11px] font-bold text-white/80 bg-[#00F5FF]/8 border border-[#00F5FF]/30 px-3.5 py-2 rounded-full">
                  {m.text} ✓
                </span>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};
